import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { listingsApi, auth } from '@/services/api';
import { actionBadge, formatAuditDate, type AuditRow } from '@/pages/dashboard/properties/components/auditFormat';

interface PropertyOption {
  id: number;
  title: string;
}

export default function AuditPage() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterId, setFilterId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const userRole = (auth.getRole() || '').toLowerCase();
  const isAdmin = ['super_admin', 'admin', 'owner'].includes(userRole);

  // Load the property list for the filter dropdown.
  useEffect(() => {
    let cancelled = false;
    listingsApi.list({ per_page: 200, page: 1 })
      .then((r) => {
        if (cancelled) return;
        const data = (r.data as Array<Record<string, unknown>> | undefined) ?? [];
        setProperties(data.map((p) => ({ id: Number(p.id), title: String(p.title || 'Untitled') })));
      })
      .catch(() => { /* filter dropdown can stay empty on error */ });
    return () => { cancelled = true; };
  }, []);

  const fetchAudit = useCallback(async (propertyId: number | null) => {
    setLoading(true);
    setError('');
    try {
      const res = await listingsApi.getAudit(propertyId ?? undefined);
      setRows((res.data ?? []) as AuditRow[]);
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchAudit(filterId);
  }, [filterId, refreshKey, isAdmin, fetchAudit]);

  const changeFilter = (id: number | null) => setFilterId(id);

  const selectedTitle = filterId ? properties.find((p) => p.id === filterId)?.title : null;

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-rose-500/15 flex items-center justify-center mb-4">
            <i className="ri-lock-2-line text-rose-400 text-2xl" />
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950">Access restricted</h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-sm">
            The audit log is admin-only (super_admin / admin / owner). Contact an administrator if you need access.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Audit Log</h1>
            <p className="text-xs text-foreground-500">
              Admin-only trail of property actions — who changed what, and when
            </p>
          </div>
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-background-200/60 bg-white text-foreground-500 hover:text-foreground-950 hover:border-accent-200 text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" /> Refresh
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-background-200/60 bg-white px-4 py-3">
          <div className="relative flex-1 max-w-xs">
            <i className="ri-filter-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <select
              value={filterId ?? ''}
              onChange={(e) => changeFilter(e.target.value ? Number(e.target.value) : null)}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer"
            >
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.id} — {p.title}</option>
              ))}
            </select>
          </div>
          {filterId && (
            <button
              onClick={() => changeFilter(null)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 border border-accent-200 hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap"
              title="Clear filter"
            >
              <i className="ri-close-line" />
              {selectedTitle || `Property #${filterId}`}
            </button>
          )}
          <span className="text-xs text-foreground-500 sm:ml-auto whitespace-nowrap">{rows.length} record(s)</span>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          {loading ? (
            <div className="p-12 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-14 bg-background-200/60 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <i className="ri-alert-line text-2xl text-rose-400 mb-2 block" />
              <p className="text-sm text-foreground-500">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <i className="ri-file-list-3-line text-3xl text-foreground-300 mb-2 block" />
              <p className="text-sm text-foreground-500">No audit entries found{filterId ? ' for the selected property' : ''}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/60">
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Action</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Property</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">By</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Details</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">When</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const badge = actionBadge(r.action);
                    return (
                      <tr key={r.id} className="border-b border-background-200/50 hover:bg-background-200/40 transition-colors">
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${badge.cls}`}>
                            <i className={badge.icon} /> {badge.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 max-w-[240px]">
                          <p className="text-xs font-medium text-foreground-950 truncate">{r.property_title || `Property #${r.property_id ?? '?'}`}</p>
                          {r.property_id && <p className="text-[10px] text-foreground-400">ID #{r.property_id}</p>}
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-600 whitespace-nowrap">{r.by_name || 'System'}</td>
                        <td className="px-5 py-3 text-xs text-foreground-500 max-w-[280px] truncate" title={r.details || ''}>{r.details || '—'}</td>
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatAuditDate(r.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}